import {
  DashboardLayout,
  DashboardMetrics,
  type PutDashboardLayoutRequest,
} from "@cloudserver/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";

export const dashboardLayoutKey = (projectId: string) =>
  ["projects", projectId, "dashboard-layout"] as const;

/** Stored positions; `dashboard.changed` events refresh them (api/events.ts). */
export const useDashboardLayout = (projectId: string) =>
  useQuery({
    queryKey: dashboardLayoutKey(projectId),
    queryFn: () => api(`/projects/${projectId}/dashboard/layout`, { schema: DashboardLayout }),
  });

/** Saves positions; the answer (the whole layout) replaces the cached one. */
export function useSaveDashboardLayout(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutDashboardLayoutRequest) =>
      api(`/projects/${projectId}/dashboard/layout`, {
        method: "PUT",
        body,
        schema: DashboardLayout,
      }),
    onSuccess: (layout) => client.setQueryData(dashboardLayoutKey(projectId), layout),
  });
}

/** How often the cards' metrics refresh (the API caches them for 55 s). */
export const DASHBOARD_METRICS_REFRESH_MS = 60_000;

/**
 * Card metrics of the servers shown on screen, in one request. Kept outside
 * the `projects` key: live events must not refetch metrics on every change.
 */
export const useDashboardMetrics = (projectId: string, serverIds: string[]) =>
  useQuery({
    queryKey: ["dashboard-metrics", projectId, serverIds],
    queryFn: () =>
      api(
        `/projects/${projectId}/dashboard/metrics?serverIds=${encodeURIComponent(serverIds.join(","))}`,
        { schema: DashboardMetrics },
      ),
    enabled: serverIds.length > 0,
    refetchInterval: DASHBOARD_METRICS_REFRESH_MS,
    // Panning changes the set of servers; the cards keep their last values meanwhile.
    placeholderData: keepPreviousData,
  });
